/**
 * Shared PCI Booking card-capture helpers — the pure, DOM-free core behind the platform capture
 * surfaces (web's `CardCaptureFrame` iframe and mobile's `CardCaptureWebView`).
 *
 * The PCI Booking hosted form runs on its own origin and posts a `CardSubmitSuccess` message back to
 * the embedding page carrying the opaque vault token plus safe display metadata (brand / last4 /
 * expiry / cardholder). The full card number never reaches Elara. Parsing that payload is deliberately
 * tolerant — PCI Booking has shipped several field-name/format variants — so both platforms share ONE
 * hardened parser rather than drifting copies. The transport (iframe `message` listener vs WebView
 * `onMessage` bridge) stays platform-specific; this module only turns a received payload into a
 * validated {@link CapturedCard} (or `null` when it can't be trusted).
 */

/** The exact origin the PCI Booking hosted capture form posts its messages from. */
export const PCI_BOOKING_ORIGIN = 'https://service.pcibooking.net';

/** Safe, non-PCI card identity captured from the hosted form. Carries NO PAN and NO CVV. */
export interface CapturedCard {
  /** Opaque PCI Booking vault token (never the raw PAN). */
  pciBookingToken: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  cardholderName: string;
}

/** First non-empty string/number among the candidate keys. */
function pickString(d: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = d[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return '';
}

/** First positive number among the candidate keys. */
function pickNumber(d: Record<string, unknown>, keys: string[]): number {
  for (const k of keys) {
    const v = d[k];
    const n = typeof v === 'number' ? v : Number.parseInt(String(v ?? ''), 10);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return Number.NaN;
}

const digitsOnly = (v: unknown): string => String(v ?? '').replaceAll(/\D/g, '');
const normalizeYear = (y: number): number => (y < 100 ? 2000 + y : y);

/** Tolerate explicit month/year fields, "MM/YY", "MM/YYYY", "MMYY", "MMYYYY", or "MM-YY". */
function parseExpiry(d: Record<string, unknown>): { expMonth: number; expYear: number } {
  const m = pickNumber(d, ['expiryMonth', 'expMonth', 'expirationMonth', 'cardExpiryMonth', 'month']);
  const y = pickNumber(d, ['expiryYear', 'expYear', 'expirationYear', 'cardExpiryYear', 'year']);
  if (Number.isFinite(m) && Number.isFinite(y)) return { expMonth: m, expYear: normalizeYear(y) };

  const raw = pickString(d, ['expiration', 'expiry', 'expirationDate', 'expiryDate', 'cardExpiry', 'expDate']);
  const digits = digitsOnly(raw);
  if (digits.length === 4)
    return { expMonth: Number(digits.slice(0, 2)), expYear: normalizeYear(Number(digits.slice(2))) };
  if (digits.length === 6) return { expMonth: Number(digits.slice(0, 2)), expYear: Number(digits.slice(2)) };
  const parts = raw.split(/[/\-.\s]+/).filter(Boolean);
  if (parts.length >= 2) {
    return { expMonth: Number.parseInt(parts[0]!, 10), expYear: normalizeYear(Number.parseInt(parts[1]!, 10)) };
  }
  return { expMonth: Number.NaN, expYear: Number.NaN };
}

/**
 * Whether a received postMessage payload is PCI Booking's `CardSubmitSuccess` object. Kept separate
 * from {@link parseCapturedCard} so a caller can distinguish "not the success message, ignore it" from
 * "the success message arrived but we couldn't read it, show an error".
 */
export function isCardSubmitSuccessMessage(data: unknown): data is Record<string, unknown> {
  return Boolean(data) && typeof data === 'object' && (data as Record<string, unknown>).messageType === 'CardSubmitSuccess';
}

/** Parse a PCI Booking `CardSubmitSuccess` payload into safe metadata; returns null if it's unusable. */
export function parseCapturedCard(d: Record<string, unknown>): CapturedCard | null {
  const token = pickString(d, ['cardToken', 'token', 'tokenLocation', 'TokenLocation']);
  const masked = pickString(d, [
    'maskedCardNumber',
    'maskedCard',
    'maskedPan',
    'cardNumber',
    'last4',
    'lastFourDigits',
  ]);
  const last4 = digitsOnly(masked).slice(-4);
  const { expMonth, expYear } = parseExpiry(d);
  if (!token || last4.length !== 4 || !(expMonth >= 1 && expMonth <= 12) || !Number.isFinite(expYear)) return null;
  return {
    pciBookingToken: token,
    brand: pickString(d, ['cardType', 'brand', 'scheme', 'cardBrand']) || 'Card',
    last4,
    expMonth,
    expYear,
    cardholderName:
      pickString(d, ['nameOnCard', 'cardHolderName', 'cardholderName', 'holderName', 'name']) || 'Cardholder',
  };
}
